import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'

interface TaxRate {
  id: string
  name: string
  rate: string
  taxType: string
  isActive: boolean
  account?: {
    code: string
    name: string
  }
}

interface Account {
  id: string
  code: string
  name: string
}

const TaxRates: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission, loading: permissionsLoading } = usePermissions(user)
  const [taxRates, setTaxRates] = useState<TaxRate[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingTaxRate, setEditingTaxRate] = useState<TaxRate | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [formData, setFormData] = useState({
    name: '',
    rate: '',
    taxType: 'VAT',
    accountId: '',
    isActive: true,
  })

  useEffect(() => {
    fetchTaxRates()
    fetchAccounts()
  }, [])

  const fetchTaxRates = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/tax-rates')
      setTaxRates(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching tax rates:', error)
      toast.error('Failed to load tax rates')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true', rootType: 'LIABILITY' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.rate || !formData.accountId) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await axios.post('/api/erp/tax-rates', {
        name: formData.name,
        rate: parseFloat(formData.rate),
        taxType: formData.taxType,
        accountId: formData.accountId,
        isActive: formData.isActive,
      })
      toast.success('Tax rate created successfully')
      setShowCreateModal(false)
      setFormData({
        name: '',
        rate: '',
        taxType: 'VAT',
        accountId: '',
        isActive: true,
      })
      fetchTaxRates()
    } catch (error: any) {
      console.error('Error creating tax rate:', error)
      toast.error(error.response?.data?.error || 'Failed to create tax rate')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingTaxRate) return

    if (!formData.name || !formData.rate || !formData.accountId) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      await axios.put(`/api/erp/tax-rates/${editingTaxRate.id}`, {
        name: formData.name,
        rate: parseFloat(formData.rate),
        taxType: formData.taxType,
        accountId: formData.accountId,
        isActive: formData.isActive,
      })
      toast.success('Tax rate updated successfully')
      setShowEditModal(false)
      setEditingTaxRate(null)
      fetchTaxRates()
    } catch (error: any) {
      console.error('Error updating tax rate:', error)
      toast.error(error.response?.data?.error || 'Failed to update tax rate')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tax rate? This action cannot be undone.')) {
      return
    }
    try {
      await axios.delete(`/api/erp/tax-rates/${id}`)
      toast.success('Tax rate deleted successfully')
      fetchTaxRates()
    } catch (error: any) {
      console.error('Error deleting tax rate:', error)
      toast.error(error.response?.data?.error || 'Failed to delete tax rate')
    }
  }

  if (permissionsLoading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading permissions...</div>
    )
  }

  if (!hasPermission('erp_tax_rate', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view tax rates.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Tax Rates</h1>
            <PageHelp pageIdentifier="tax-rates" title="Tax Rates" />
          </div>
          <p className="text-gray-600 mt-1">Manage tax rates and tax accounts</p>
        </div>
        {hasPermission('erp_tax_rate', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Tax Rate</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : taxRates.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500">No tax rates found. Create your first tax rate to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {taxRates.map((taxRate) => (
                <tr key={taxRate.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{taxRate.name}</td>
                  <td className="px-4 py-2">{parseFloat(taxRate.rate).toFixed(2)}%</td>
                  <td className="px-4 py-2">{taxRate.taxType}</td>
                  <td className="px-4 py-2">
                    {taxRate.account ? `${taxRate.account.code} - ${taxRate.account.name}` : '-'}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      taxRate.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {taxRate.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex space-x-2">
                      {hasPermission('erp_tax_rate', 'write') && (
                        <button
                          onClick={() => {
                            setEditingTaxRate(taxRate)
                            setFormData({
                              name: taxRate.name,
                              rate: taxRate.rate,
                              taxType: taxRate.taxType,
                              accountId: (taxRate.account as any)?.id || '',
                              isActive: taxRate.isActive,
                            })
                            setShowEditModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {hasPermission('erp_tax_rate', 'delete') && (
                        <button
                          onClick={() => handleDelete(taxRate.id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Tax Rate Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Create Tax Rate</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., VAT 20%, Sales Tax 5%"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    required
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    placeholder="20.00"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Type *</label>
                  <select
                    required
                    value={formData.taxType}
                    onChange={(e) => setFormData({ ...formData, taxType: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="VAT">VAT</option>
                    <option value="SALES_TAX">Sales Tax</option>
                    <option value="GST">GST</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Account *</label>
                  <select
                    required
                    value={formData.accountId}
                    onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select tax account...</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Create Tax Rate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tax Rate Modal */}
      {showEditModal && editingTaxRate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Edit Tax Rate</h2>
            <form onSubmit={handleUpdate}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., VAT 20%, Sales Tax 5%"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rate (%) *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    required
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    placeholder="20.00"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Type *</label>
                  <select
                    required
                    value={formData.taxType}
                    onChange={(e) => setFormData({ ...formData, taxType: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="VAT">VAT</option>
                    <option value="SALES_TAX">Sales Tax</option>
                    <option value="GST">GST</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tax Account *</label>
                  <select
                    required
                    value={formData.accountId}
                    onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Select tax account...</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingTaxRate(null)
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Update Tax Rate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default TaxRates
